// Batched voxel unit renderer — one InstancedMesh per prefab-kind (P0.1-full).
//
// Each instance is the WHOLE prefab (vs VoxelUnit's per-entity InstancedMesh
// where each instance was one voxel). 50 skeletons on the battlefield go
// from ~50 draw calls to 1.
//
// Standalone for now — not yet wired into VoxelUnits. Day 2 of the P0.1-full
// plan replaces VoxelUnit with grouped VoxelUnitBatch instances.
//
// See docs/superpowers/plans/2026-06-23-voxel-batching-p0_1-full.md.
import { playerColor } from '@aod/ui';
import { VOXEL_SCALE } from '@aod/ui/prefabs';
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useSettings } from '../settings/useSettings.js';
import { RenderLayer } from './RenderLayer.js';
import { getUnitModelGeometry, useUnitModelsVersion } from './unitModels.js';
import { selectionStrength, unitRenderStyle } from './unitRenderStyle.js';
import { getCreatureGeometry } from './voxelGeometryCache.js';
import { getVoxelMaterial } from './voxelMaterial.js';

const FLASH_DURATION_MS = 300;

// Module-level temporaries — mutated in place, never reallocated.
const _dummy = new THREE.Object3D();
const _factionColor = new THREE.Color();

/**
 * Per-unit state for batched rendering. All units in one batch share the
 * same prefabId. Callers (e.g. a future VoxelUnits refactor) are responsible
 * for tracking `flashStart` — it's input, not internal state.
 */
export interface BatchedUnit {
  readonly id: string;
  readonly ownerId?: number;
  /**
   * The colour this unit wears, when it is not its owner's faction colour —
   * a foreign house paints its own hull. Falls back to `playerColor(ownerId)`.
   */
  readonly factionColor?: string;
  /** World-space X — typically unit.transform.position[0]. */
  readonly baseX: number;
  /** World-space Z — typically unit.transform.position[2]. */
  readonly baseZ: number;
  /** World-space Y — ground elevation for land units, water-surface for naval. */
  readonly baseY: number;
  readonly hp: number;
  readonly hpMax: number;
  readonly selected: boolean;
  readonly moving: boolean;
  /** Active river-edge crossing — lerp from the departed tile centre toward
   * (baseX, baseZ), yawing along the travel direction instead of billboarding.
   * Null once the settle window ends (or for non-ship units). */
  readonly crossing: {
    readonly fromX: number;
    readonly fromZ: number;
    readonly startMs: number;
    readonly untilMs: number;
    readonly yaw: number;
  } | null;
  /** Tallest voxel y in the prefab — used for HP bar placement. */
  readonly prefabHeight: number;
  /** performance.now() timestamp when damage flash started; 0 = not flashing. */
  readonly flashStart: number;
  /** Sim tick at which position jitter stops being applied; 0 = no jitter. */
  readonly jitterUntilTick: number;
  /** Signed world-unit offset along the step axis (positive = forward, negative = back). */
  readonly stepOffsetWorld: number;
  /** Direction of the most recent step (used by the renderer to pick the offset axis). */
  readonly stepDirection: 'forward' | 'back' | null;
}

interface VoxelUnitBatchProps {
  readonly prefabId: string;
  readonly units: ReadonlyArray<BatchedUnit>;
  readonly currentTick: number;
  readonly onClick?: (unitId: string) => void;
  /** Hover in/out — null when the pointer leaves the batch. */
  readonly onHover?: (unitId: string | null) => void;
}

const JITTER_AMPLITUDE_WORLD = 0.05;
const JITTER_FREQ_HZ = 12;

export function VoxelUnitBatch({
  prefabId,
  units,
  currentTick,
  onClick,
  onHover,
}: VoxelUnitBatchProps) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const style = unitRenderStyle(prefabId);

  // Clone the cached base geometry so this batch can attach its own
  // per-instance attributes without polluting the shared cache.
  const quality = useSettings((s) => s.unitQuality);
  const hiFi = quality !== 'voxel';
  const meshes = quality === 'mesh';
  useUnitModelsVersion(meshes); // a loaded model swaps in on the next render
  // At the 3D-models level an authored model, once loaded, replaces the smooth voxel mesh.
  const baseGeometry =
    (meshes ? getUnitModelGeometry(prefabId) : null) ?? getCreatureGeometry(prefabId, hiFi);
  const geometry = useMemo(() => baseGeometry?.clone(), [baseGeometry]);
  const material = useMemo(() => getVoxelMaterial(), []);

  // Per-instance buffers — re-allocated only when length changes.
  const factionArr = useMemo(() => new Float32Array(units.length * 3), [units.length]);
  const selArr = useMemo(() => new Float32Array(units.length), [units.length]);
  const flashArr = useMemo(() => new Float32Array(units.length), [units.length]);

  // Attach instanced attributes (re-runs when geometry or length changes).
  useEffect(() => {
    if (!geometry) return;
    geometry.setAttribute('aFactionColor', new THREE.InstancedBufferAttribute(factionArr, 3));
    geometry.setAttribute('aSelected', new THREE.InstancedBufferAttribute(selArr, 1));
    geometry.setAttribute('aFlash', new THREE.InstancedBufferAttribute(flashArr, 1));
  }, [geometry, factionArr, selArr, flashArr]);

  // Per-state-change: write faction color / selection / flash values.
  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh || !geometry) return;

    const now = performance.now();
    for (let i = 0; i < units.length; i++) {
      const u = units[i];
      if (!u) continue;
      _factionColor.set(u.factionColor ?? playerColor(u.ownerId));
      factionArr[i * 3] = _factionColor.r;
      factionArr[i * 3 + 1] = _factionColor.g;
      factionArr[i * 3 + 2] = _factionColor.b;
      selArr[i] = selectionStrength(prefabId, u.selected);
      if (u.flashStart > 0) {
        const elapsed = now - u.flashStart;
        flashArr[i] = elapsed < FLASH_DURATION_MS ? 1 - elapsed / FLASH_DURATION_MS : 0;
      } else {
        flashArr[i] = 0;
      }
    }
    const fa = geometry.attributes.aFactionColor as THREE.BufferAttribute | undefined;
    const sa = geometry.attributes.aSelected as THREE.BufferAttribute | undefined;
    const fla = geometry.attributes.aFlash as THREE.BufferAttribute | undefined;
    if (fa) fa.needsUpdate = true;
    if (sa) sa.needsUpdate = true;
    if (fla) fla.needsUpdate = true;
    mesh.count = units.length;
  }, [units, geometry, factionArr, selArr, flashArr, prefabId]);

  // Per-frame: billboard (face camera) + walk/idle bob + combat jitter/step.
  useFrame(({ camera, clock }) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const t = clock.elapsedTime;
    for (let i = 0; i < units.length; i++) {
      const u = units[i];
      if (!u) continue;
      const m = u.moving;
      // Walk-cycle tempo: ~2.7 rad/s (≈0.43 Hz) — the old 8 rad/s read as
      // jittery hopping and made busy maps hard to look at.
      const [bobFreq, bobAmp] = m ? style.moveBob : style.idleBob;
      const yBob = Math.sin(t * bobFreq) * bobAmp;
      const xSway = m ? Math.sin(t * 2.7) * 0.02 : 0;

      // Combat jitter — active while currentTick is within the jitter window.
      const jitterActive = u.jitterUntilTick > 0 && currentTick <= u.jitterUntilTick;
      const xJitter = jitterActive ? Math.sin(t * JITTER_FREQ_HZ) * JITTER_AMPLITUDE_WORLD : 0;

      // Step offset applied along the world Z axis. v1 simplification —
      // a proper direction would require attacker position; spec defers
      // that to a follow-up.
      const stepOffsetZ = u.stepOffsetWorld;

      // River-edge crossings ease the sprite from the departed tile centre
      // to the new one (through the shared edge channel) and hold a fixed
      // heading along the travel direction.
      let px = u.baseX;
      let pz = u.baseZ;
      let yaw: number | null = null;
      const c = u.crossing;
      if (c) {
        const now = performance.now();
        const raw = Math.min(Math.max((now - c.startMs) / (c.untilMs - c.startMs), 0), 1);
        const e = raw * raw * (3 - 2 * raw); // smoothstep
        px = c.fromX + (u.baseX - c.fromX) * e;
        pz = c.fromZ + (u.baseZ - c.fromZ) * e;
        yaw = c.yaw;
      }

      _dummy.position.set(px + xSway + xJitter, u.baseY + style.lift + yBob, pz + stepOffsetZ);
      _dummy.rotation.y = yaw ?? Math.atan2(camera.position.x - px, camera.position.z - pz);
      _dummy.scale.setScalar(style.scale);
      _dummy.updateMatrix();
      mesh.setMatrixAt(i, _dummy.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
  });

  if (!geometry) return null;

  return (
    <>
      <instancedMesh
        ref={meshRef}
        args={[geometry, material, units.length]}
        onClick={(e) => {
          e.stopPropagation();
          const idx = e.instanceId;
          if (idx === undefined) return;
          const u = units[idx];
          if (u) onClick?.(u.id);
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          const idx = e.instanceId;
          if (idx === undefined) return;
          const u = units[idx];
          if (u) onHover?.(u.id);
        }}
        onPointerOut={() => onHover?.(null)}
        renderOrder={RenderLayer.Units}
        frustumCulled={false}
      />
      {units.map((u) => (
        <UnitOverlay
          key={u.id}
          baseX={u.baseX}
          baseZ={u.baseZ}
          baseY={u.baseY}
          prefabHeightVoxels={u.prefabHeight * style.scale + style.lift / VOXEL_SCALE}
          hp={u.hp}
          hpMax={u.hpMax}
          selected={u.selected}
          teamColor={playerColor(u.ownerId)}
        />
      ))}
    </>
  );
}

/** HP bar + selection ring overlays for one batched unit. */
interface UnitOverlayProps {
  readonly baseX: number;
  readonly baseZ: number;
  readonly baseY: number;
  readonly prefabHeightVoxels: number;
  readonly hp: number;
  readonly hpMax: number;
  readonly selected: boolean;
  /** Owner colour for the ground ring (friend/foe at a glance). */
  readonly teamColor: string;
}

// Shared across every unit: the ring is tinted per mesh via its material.
const teamDiscGeo = new THREE.CircleGeometry(0.34, 24).rotateX(-Math.PI / 2);
const teamRimGeo = new THREE.RingGeometry(0.3, 0.36, 32).rotateX(-Math.PI / 2);

function UnitOverlay({
  baseX,
  baseZ,
  baseY,
  prefabHeightVoxels,
  hp,
  hpMax,
  selected,
  teamColor,
}: UnitOverlayProps) {
  const hpPct = hpMax > 0 ? Math.max(0, hp / hpMax) : 0;
  const barW = 0.5;
  const barH = 0.06;
  const barY = baseY + prefabHeightVoxels * VOXEL_SCALE + 0.2;
  const fillColor = hpPct > 0.5 ? '#44cc44' : hpPct > 0.25 ? '#ccaa44' : '#cc4444';

  return (
    <group position={[baseX, 0, baseZ]}>
      {/* HP bar background */}
      <mesh position={[0, barY, 0]}>
        <boxGeometry args={[barW, barH, 0.02]} />
        <meshBasicMaterial color="#333333" transparent opacity={0.7} />
      </mesh>
      {/* HP bar fill */}
      <mesh position={[-(barW / 2) * (1 - hpPct), barY, 0.001]}>
        <boxGeometry args={[barW * hpPct, barH, 0.025]} />
        <meshBasicMaterial color={fillColor} />
      </mesh>
      {/* Team ring: faint disc + crisper rim at the unit's feet, so owner
          reads at a glance even on prefabs without team voxels. */}
      <mesh geometry={teamDiscGeo} position={[0, baseY + 0.012, 0]} renderOrder={1}>
        <meshBasicMaterial color={teamColor} transparent opacity={0.22} depthWrite={false} />
      </mesh>
      <mesh geometry={teamRimGeo} position={[0, baseY + 0.014, 0]} renderOrder={1}>
        <meshBasicMaterial color={teamColor} transparent opacity={0.75} depthWrite={false} />
      </mesh>
      {/* Selection ring */}
      {selected && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, baseY - 0.01, 0]}>
          <ringGeometry args={[0.35, 0.42, 32]} />
          <meshBasicMaterial color="#ffd966" side={THREE.DoubleSide} />
        </mesh>
      )}
    </group>
  );
}
